import React, { useMemo } from "react";
import { useImmer } from "use-immer";

import {
  AnalyzeTinybirdResourceResponseBodyType,
  GraphConfig,
  GraphType,
} from "@intelligems/ig-types";
import { timeSeriesFormatter } from "@src/api/analytics/dashboard-tinybird";
import { useGetTinybirdExperienceAnalysis } from "@src/api/analytics/get-tinybird-experience-analysis";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useOrgTimezone } from "@src/api/organizations/get-organization";
import { TimeSeriesGraph } from "@src/features/analytics/metricDetails/timeSeries/TimeSeriesGraph";
import { hasFiltersBeenModified } from "@src/features/analytics/utils";
import { useSelector } from "@src/store";
import { selectedExperience } from "@src/store/slices/experiences";
import { getExperienceRunTimeNumeric } from "@src/utils/dateConverters";
import { UseQueryResult } from "@tanstack/react-query";

interface TimeSeriesGraphWrapperProps {
  selectedMetricId: string;
  graphConfigs: GraphConfig[];
  overviewDataLoaded: boolean;
}

export const TimeSeriesGraphWrapper = ({
  selectedMetricId,
  graphConfigs,
  overviewDataLoaded,
}: TimeSeriesGraphWrapperProps) => {
  const timezone = useOrgTimezone();
  const experience = useSelector(selectedExperience);
  const dashboardFilters = useSelector((state) => state.dashboardFilters);
  const duration = getExperienceRunTimeNumeric({
    experienceStatus: experience?.status,
    experienceActions: experience?.experienceActions || [],
    timezone: timezone,
    dashboardEndTs: dashboardFilters.endTime as number | null | undefined,
    dashboardStartTs: dashboardFilters.startTime as number | null | undefined,
  });

  const [graphInterval, setGraphInterval] = useImmer<string>(
    duration < 5 ? "HOUR" : duration < 21 ? "DAY" : "WEEK"
  );
  const [cumulative, setCumulative] = useImmer(true);
  const [confidenceInterval, setConfidenceInterval] = useImmer(false);

  const timeSeriesDataQuery: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[] =
    useGetTinybirdExperienceAnalysis(
      {
        graphConfigs: [
          {
            title: ["Time Series"],
            metrics: graphConfigs.map((c) => c.metrics).flat(),
            dataTypes: ["currency"],
            datasetId: cumulative
              ? "variation_timeseries_cumulative"
              : "variation_timeseries_discrete",
            graphType: GraphType.Bar,
            isImpactGraph: true,
            filterMetric: "dt",
          },
        ],
        experience,
        interval: graphInterval,
        overviewDataLoaded,
      },
      "experiences"
    );

  const shouldNotRenderGraphs = useMemo(() => {
    return (
      timeSeriesDataQuery &&
      timeSeriesDataQuery[0].isSuccess &&
      (timeSeriesDataQuery[0].data?.metrics?.length ?? 0) === 0 &&
      !hasFiltersBeenModified(dashboardFilters)
    );
  }, [timeSeriesDataQuery, dashboardFilters]);

  const selectedTimeSeriesData = useMemo(() => {
    if (!shouldNotRenderGraphs && timeSeriesDataQuery[0].data) {
      return timeSeriesFormatter(
        timeSeriesDataQuery[0].data?.metrics || [],
        selectedMetricId,
        "dt",
        graphInterval
      );
    } else {
      return [];
    }
  }, [
    shouldNotRenderGraphs,

    timeSeriesDataQuery[0].data,
    selectedMetricId,
    cumulative,
    graphInterval,
  ]);

  return (
    <TimeSeriesGraph
      graphConfigs={graphConfigs}
      graphInterval={graphInterval}
      setGraphInterval={setGraphInterval}
      data={selectedTimeSeriesData}
      cumulative={cumulative}
      setCumulative={setCumulative}
      confidenceInterval={confidenceInterval}
      setConfidenceInterval={setConfidenceInterval}
      selectedMetricId={selectedMetricId}
      isLoading={
        timeSeriesDataQuery[0].isLoading || timeSeriesDataQuery[0].isPending
      }
    />
  );
};
